import { useMemo, useCallback } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { useLingui as useCoreLingui } from "@lingui/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, FileText, Lock, Plus } from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState } from "@/components/table/EmptyState";
import { useDocumentDownload } from "@/hooks/useDocumentDownload";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig, FilterConfig } from "@/components/table/types";
import type { Document } from "@/types/document";
import {
  CONFIDENTIALITY_LEVELS,
  DOCUMENT_CATEGORIES,
} from "@/types/document";
import { DocumentAvatarCell } from "@/utils/avatarColumnHelpers";
import {
  CONFIDENTIALITY_LABELS,
  CONFIDENTIALITY_TONES,
  DOCUMENT_CATEGORY_LABELS,
  isCategory,
  isConfidentiality,
} from "./documentLabels";

interface DocumentsTableProps {
  documents: Document[];
  onEdit: (doc: Document) => void;
  /** Opens the create sheet from the first-use empty state. */
  onAdd?: () => void;
}

export function DocumentsTable({ documents, onEdit, onAdd }: DocumentsTableProps) {
  const { t } = useLingui();
  // i18n (the core instance) is needed alongside `t` for the one message that
  // carries a placeholder: the macro form would extract `${}` as a positional
  // argument that never binds.
  const { i18n } = useCoreLingui();
  // The AUDITED download path (issue #1297): it issues a fresh presigned URL and
  // writes a `download` access-log row. The row's own `download_url` is used only
  // as the attached-file gate, exactly as before.
  const downloadMutation = useDocumentDownload();
  const downloadDocument = downloadMutation.mutate;
  const handleDownload = useCallback(
    (documentId: string) => downloadDocument(documentId),
    [downloadDocument],
  );

  const columns = useMemo(
    (): EditableCellConfig<Document>[] => [
      {
        accessorKey: "id",
        id: "avatar",
        header: "",
        editable: false,
        sortable: false,
        inputType: "text",
        enableHiding: false,
        width: 60,
        cell: ({ row }) => (
          <DocumentAvatarCell
            document={row.original}
            onAvatarClick={onEdit}
          />
        ),
      },
      {
        accessorKey: "name",
        id: "name",
        header: t({ id: "documents.fields.name", message: "Name" }),
        editable: false,
        sortable: true,
        inputType: "text",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        accessorKey: "category",
        id: "category",
        header: t({ id: "documents.fields.category", message: "Category" }),
        editable: false,
        sortable: true,
        inputType: "select",
        selectOptions: DOCUMENT_CATEGORIES.map((c) => ({
          value: c.value,
          label: i18n._(DOCUMENT_CATEGORY_LABELS[c.value]),
        })),
        width: 140,
        cell: ({ row }) => {
          const category = row.original.category;
          return (
            <Badge variant="outline">
              {isCategory(category) ? i18n._(DOCUMENT_CATEGORY_LABELS[category]) : category}
            </Badge>
          );
        },
      },
      {
        accessorKey: "confidentiality",
        id: "confidentiality",
        header: t({ id: "documents.fields.confidentiality", message: "Confidentiality" }),
        editable: false,
        sortable: true,
        inputType: "select",
        selectOptions: CONFIDENTIALITY_LEVELS.map((c) => ({
          value: c.value,
          label: i18n._(CONFIDENTIALITY_LABELS[c.value]),
        })),
        width: 140,
        cell: ({ row }) => {
          const level = row.original.confidentiality;
          if (!isConfidentiality(level)) return <span>{level}</span>;
          return (
            <StatusBadge
              tone={CONFIDENTIALITY_TONES[level]}
              icon={level === "confidential" ? Lock : undefined}
              label={i18n._(CONFIDENTIALITY_LABELS[level])}
            />
          );
        },
      },
      {
        accessorKey: "is_published",
        id: "is_published",
        header: t({ id: "documents.fields.published", message: "Published" }),
        editable: false,
        sortable: true,
        inputType: "select",
        selectOptions: [
          { value: "true", label: t({ id: "common.yes", message: "Yes" }) },
          { value: "false", label: t({ id: "common.no", message: "No" }) },
        ],
        width: 100,
        cell: ({ row }) => (
          <StatusBadge
            tone={row.original.is_published ? "success" : "neutral"}
            label={
              row.original.is_published ? (
                <Trans id="common.yes">Yes</Trans>
              ) : (
                <Trans id="common.no">No</Trans>
              )
            }
          />
        ),
      },
      {
        accessorKey: "current_version",
        id: "current_version",
        header: t({ id: "documents.fields.version", message: "Version" }),
        editable: false,
        sortable: true,
        inputType: "number",
        width: 80,
        cell: ({ row }) => <span>v{row.original.current_version}</span>,
      },
      {
        accessorKey: "filename",
        id: "filename",
        header: t({ id: "documents.fields.file", message: "File" }),
        editable: false,
        sortable: false,
        inputType: "text",
        cell: ({ row }) => {
          if (!row.original.filename) {
            return (
              <span className="text-muted-foreground text-sm">
                <Trans id="documents.noFile">No file</Trans>
              </span>
            );
          }
          return (
            <div className="flex items-center gap-2">
              <span className="truncate max-w-[150px] text-sm">
                {row.original.filename}
              </span>
              {row.original.download_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-primary hover:text-primary/80"
                  aria-label={i18n._({
                    id: "documents.download.aria",
                    message: "Download {filename}",
                    values: { filename: row.original.filename },
                  })}
                  onClick={() => handleDownload(row.original.id ?? "")}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [t, i18n, onEdit, handleDownload]
  );

  const filterConfig = useMemo(
    (): FilterConfig<Document> => ({
      category: "select",
      confidentiality: "select",
      is_published: "select",
    }),
    []
  );

  const handleCellUpdate = useCallback(
    async (_rowId: string, _field: string, _newValue: unknown) => {
      // Documents use sheet-based editing, not inline
    },
    []
  );

  return (
    <BaseEditableTable<Document>
      data={documents}
      columns={columns}
      onCellUpdate={handleCellUpdate}
      defaultSortField="name"
      defaultSortOrder="asc"
      defaultPageSize={20}
      enableSearch={true}
      enableFilters={true}
      filterConfig={filterConfig}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="documents-table"
      emptyState={
        <EmptyState
          icon={<FileText className="h-10 w-10" />}
          title={<Trans id="documents.empty.title">No documents yet</Trans>}
          description={
            <Trans id="documents.empty.description">
              Add the regulations, guidelines and info sheets your team and families need.
            </Trans>
          }
          action={
            onAdd ? (
              <Button size="sm" onClick={onAdd}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                <Trans id="documents.create.button">New Document</Trans>
              </Button>
            ) : undefined
          }
        />
      }
    />
  );
}
